import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import hotkeys, { type HotkeysEvent } from 'hotkeys-js';
import {
  Captions,
  Check,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Keyboard,
  Languages,
  Loader2,
  Maximize,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  RectangleHorizontal,
  RotateCcw,
  RotateCw,
  Settings,
  SlidersHorizontal,
  Volume1,
  Volume2,
  VolumeX,
  WifiOff,
  X,
} from 'lucide-react';
import api from '@/lib/api';
import VideoWatermarkOverlay from './VideoWatermarkOverlay';
import EndOfVideoCard from './EndOfVideoCard';
import ResumeToast from './ResumeToast';
import { PREF, formatPlayerTime, readBoolPref, readNumberPref, readPref, writePref } from './playerPrefs';
import { isConstrainedConnection, useOnlineStatus } from './useNetworkStatus';

const WATERMARK_LOGO_URL = '/branding/alas-by-satelab.png';

type VideoQuality = {
  name?: string;
  width?: number;
  height?: number;
  bitrate_kbps?: number;
};

type MetaAudioTrack = {
  language_code: string;
  label: string;
  is_original: boolean;
  captions_url: string | null;
};

type VideoMetaResponse = {
  status: 'uploading' | 'processing' | 'ready' | 'error';
  hls_url: string;
  thumbnails: string[];
  preview_vtt_url: string | null;
  sprite_url: string | null;
  duration: number;
  qualities: VideoQuality[];
  audio_tracks: MetaAudioTrack[];
  watermark_code: string | null;
};

type PreviewCue = {
  start: number;
  end: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

type Props = {
  videoId: string;
  title: string;
  initialTime?: number;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  /** Siguiente lección accesible: habilita la tarjeta de cierre, la tecla N y el control multimedia del sistema. */
  nextLesson?: { title: string; onNext: () => void } | null;
  onPrevious?: () => void;
  /** Modo teatro (escritorio): lo controla la página; el reproductor solo ofrece el botón y la tecla T. */
  theater?: boolean;
  onToggleTheater?: () => void;
  autoPlay?: boolean;
};

const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const VOLUME_STORAGE_KEY = 'academy:player:volume';
const HIDE_CONTROLS_DELAY_MS = 2600;
const STALL_NOTICE_MS = 8000;
const DOUBLE_TAP_MS = 300;
const SEEK_CHAIN_MS = 700;
const RESUME_MIN_SECONDS = 5;
const RESUME_END_MARGIN_SECONDS = 10;
const formatTime = formatPlayerTime;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

function closestInteractive(target: EventTarget | null, selector: string): boolean {
  return target instanceof HTMLElement && Boolean(target.closest(selector));
}

function parseTimestamp(timestamp: string): number {
  const parts = timestamp.split(':').map((p) => p.trim());
  if (parts.length !== 3) {
    return 0;
  }
  const [hours, minutes, secondsPart] = parts;
  const seconds = Number.parseFloat(secondsPart);
  return Number.parseInt(hours, 10) * 3600 + Number.parseInt(minutes, 10) * 60 + seconds;
}

function parsePreviewVtt(vttText: string): PreviewCue[] {
  const lines = vttText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const cues: PreviewCue[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.includes('-->')) {
      continue;
    }

    const [startRaw, endRaw] = line.split('-->').map((part) => part.trim());
    const coordsLine = lines[i + 1];
    const coordsMatch = coordsLine?.match(/#xywh=(\d+),(\d+),(\d+),(\d+)/);
    if (!coordsMatch) {
      continue;
    }

    cues.push({
      start: parseTimestamp(startRaw),
      end: parseTimestamp(endRaw),
      x: Number.parseInt(coordsMatch[1], 10),
      y: Number.parseInt(coordsMatch[2], 10),
      width: Number.parseInt(coordsMatch[3], 10),
      height: Number.parseInt(coordsMatch[4], 10),
    });
  }

  return cues;
}

function readStoredVolume(): number {
  try {
    const raw = localStorage.getItem(VOLUME_STORAGE_KEY);
    const parsed = raw ? Number.parseFloat(raw) : NaN;
    return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
  } catch {
    return 1;
  }
}

function writeStoredVolume(value: number) {
  try {
    localStorage.setItem(VOLUME_STORAGE_KEY, String(value));
  } catch {
    // best-effort — private mode / disabled storage shouldn't break playback
  }
}

type SettingsPane = 'closed' | 'main' | 'quality' | 'speed' | 'audio' | 'shortcuts';

const SHORTCUTS_LEGEND: Array<{ keys: string; label: string }> = [
  { keys: 'Espacio / K', label: 'Reproducir / Pausar' },
  { keys: '← / →', label: 'Retroceder / Avanzar 5 s' },
  { keys: 'J / L', label: 'Retroceder / Avanzar 10 s' },
  { keys: '↑ / ↓', label: 'Subir / Bajar volumen' },
  { keys: 'M', label: 'Silenciar' },
  { keys: 'C', label: 'Activar / Desactivar subtítulos' },
  { keys: ', / .', label: 'Bajar / Subir velocidad' },
  { keys: 'F', label: 'Pantalla completa' },
  { keys: 'T', label: 'Modo teatro (escritorio)' },
  { keys: 'P', label: 'Imagen en imagen' },
  { keys: 'N', label: 'Siguiente lección' },
  { keys: '0-9', label: 'Saltar al 0 %-90 % del video' },
  { keys: 'Inicio / Fin', label: 'Ir al inicio / final' },
  { keys: '?', label: 'Mostrar esta lista' },
  { keys: 'Esc', label: 'Cerrar menú / salir de pantalla completa' },
];
type SeekFlash = { side: 'left' | 'right'; id: number; amount: number } | null;
type HlsAudioTrack = { index: number; lang: string; name: string };
type TapZone = 'left' | 'center' | 'right';

export default function NetflixHlsPlayer({
  videoId,
  title,
  initialTime,
  onEnded,
  onTimeUpdate,
  nextLesson,
  onPrevious,
  theater = false,
  onToggleTheater,
  autoPlay = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const scrubberRef = useRef<HTMLDivElement | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const clickTimeoutRef = useRef<number | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);

  // Keep callback refs fresh so event listeners never call stale closures
  const onEndedRef = useRef(onEnded);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const initialTimeRef = useRef(initialTime ?? 0);
  useLayoutEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);
  useLayoutEffect(() => { onTimeUpdateRef.current = onTimeUpdate; }, [onTimeUpdate]);
  useLayoutEffect(() => { initialTimeRef.current = initialTime ?? 0; }, [initialTime]);

  const nextLessonRef = useRef(nextLesson);
  const onPreviousRef = useRef(onPrevious);
  const onToggleTheaterRef = useRef(onToggleTheater);
  const autoPlayRef = useRef(autoPlay);
  useLayoutEffect(() => { nextLessonRef.current = nextLesson; }, [nextLesson]);
  useLayoutEffect(() => { onPreviousRef.current = onPrevious; }, [onPrevious]);
  useLayoutEffect(() => { onToggleTheaterRef.current = onToggleTheater; }, [onToggleTheater]);
  useLayoutEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);
  const hasNextLesson = Boolean(nextLesson);
  const hasPrevious = Boolean(onPrevious);

  // Reanudación: se aplica una sola vez por carga del stream, venga primero el
  // manifiesto o la posición guardada (llegan en peticiones separadas).
  const resumeAppliedRef = useRef(false);
  const manifestReadyRef = useRef(false);
  const retryTargetRef = useRef<number | null>(null);
  const durationRef = useRef(0);
  const metaRequestRef = useRef(0);
  const lastPointerTypeRef = useRef<string>('mouse');
  const lastTapRef = useRef<{ time: number; zone: TapZone }>({ time: 0, zone: 'center' });
  const lastSeekTapRef = useRef<{ time: number; zone: TapZone }>({ time: 0, zone: 'center' });
  const tapTimerRef = useRef<number | null>(null);
  const stallTimerRef = useRef<number | null>(null);
  const noteTimeoutRef = useRef<number | null>(null);
  const wasOfflineRef = useRef(false);
  const wasPlayingOfflineRef = useRef(false);
  const positionUpdateRef = useRef(0);
  const hintShownRef = useRef(false);
  const online = useOnlineStatus();

  const [meta, setMeta] = useState<VideoMetaResponse | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedPercent, setBufferedPercent] = useState(0);
  const [levels, setLevels] = useState<Array<{ index: number; label: string }>>([]);
  const [selectedLevel, setSelectedLevel] = useState(-1);
  const [hlsAudioTracks, setHlsAudioTracks] = useState<HlsAudioTrack[]>([]);
  const [selectedAudioIndex, setSelectedAudioIndex] = useState(-1);
  const [captionsEnabled, setCaptionsEnabled] = useState(() => readBoolPref(PREF.captions, false));
  const [cuesVersion, setCuesVersion] = useState(0);
  const [resumeToast, setResumeToast] = useState<number | null>(null);
  const [slowNotice, setSlowNotice] = useState(false);
  const [ended, setEnded] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [previewCues, setPreviewCues] = useState<PreviewCue[]>([]);
  const [hoveredRatio, setHoveredRatio] = useState<number | null>(null);
  const [isHoveringScrubber, setIsHoveringScrubber] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPiP, setIsPiP] = useState(false);
  const [pipSupported, setPipSupported] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showRemaining, setShowRemaining] = useState(false);
  const [settingsPane, setSettingsPane] = useState<SettingsPane>('closed');
  const [seekFlash, setSeekFlash] = useState<SeekFlash>(null);

  // `silent` (reintento tras un corte): vuelve a pedir el meta —los tokens de
  // streaming son de vida corta— sin desmontar el <video> ni perder sus listeners.
  const loadMeta = useCallback((silent: boolean) => {
    const requestId = ++metaRequestRef.current;
    if (!silent) {
      setLoadingMeta(true);
      setError(null);
    }

    api
      .get(`/academy/videos/${videoId}/meta`)
      .then(({ data }) => {
        if (requestId !== metaRequestRef.current) return;
        const payload = data.data as VideoMetaResponse;
        setMeta(payload);
        setDuration(payload.duration || 0);
        durationRef.current = payload.duration || 0;
        if (silent) {
          setFatalError(null);
          setRetryKey((key) => key + 1);
        }
      })
      .catch((err: unknown) => {
        if (requestId !== metaRequestRef.current) return;
        console.error(err);
        if (silent) {
          setFatalError('No pudimos renovar el acceso al video. Verifica tu conexión e inténtalo de nuevo.');
        } else {
          setError('No se pudo cargar el video de esta lección.');
        }
      })
      .finally(() => {
        if (requestId === metaRequestRef.current && !silent) {
          setLoadingMeta(false);
        }
      });
  }, [videoId]);

  useEffect(() => {
    loadMeta(false);
    return () => {
      metaRequestRef.current += 1;
    };
  }, [loadMeta]);

  const flashNote = useCallback((text: string) => {
    if (noteTimeoutRef.current) window.clearTimeout(noteTimeoutRef.current);
    setNote(text);
    noteTimeoutRef.current = window.setTimeout(() => setNote(null), 1400);
  }, []);

  const applyResume = useCallback(() => {
    const video = videoRef.current;
    if (!video || resumeAppliedRef.current) return;
    const retryTarget = retryTargetRef.current;
    const target = retryTarget ?? initialTimeRef.current;
    const total = durationRef.current;
    if (!(target > RESUME_MIN_SECONDS)) return;
    if (total > 0 && target >= total - RESUME_END_MARGIN_SECONDS) return;
    // Si el estudiante ya empezó a ver desde el principio, no lo devolvemos.
    if (retryTarget === null && video.currentTime >= 3) {
      resumeAppliedRef.current = true;
      return;
    }
    resumeAppliedRef.current = true;
    video.currentTime = target;
    if (retryTarget !== null) {
      retryTargetRef.current = null;
    } else {
      setResumeToast(target);
    }
  }, []);

  // La posición guardada puede llegar después del manifiesto: aplicarla cuando llegue.
  useEffect(() => {
    if (manifestReadyRef.current || (videoRef.current?.readyState ?? 0) >= 1) {
      applyResume();
    }
  }, [initialTime, applyResume]);

  useEffect(() => {
    if (!meta?.preview_vtt_url) {
      setPreviewCues([]);
      return;
    }

    let cancelled = false;
    fetch(meta.preview_vtt_url)
      .then((res) => res.text())
      .then((text) => {
        if (cancelled) return;
        setPreviewCues(parsePreviewVtt(text));
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewCues([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [meta?.preview_vtt_url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !meta?.hls_url) {
      return;
    }

    setFatalError(null);
    manifestReadyRef.current = false;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const instance = new Hls({
        enableWorker: true,
        capLevelToPlayerSize: true,
        // Ahorro de datos o conexión lenta: arrancar en la calidad más baja.
        ...(isConstrainedConnection() ? { startLevel: 0 } : {}),
      });

      instance.attachMedia(video);
      instance.on(Hls.Events.MEDIA_ATTACHED, () => {
        instance.loadSource(meta.hls_url);
      });

      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        const availableLevels = instance.levels.map((level, index) => ({
          index,
          label: level.height ? `${level.height}p` : `Nivel ${index + 1}`,
        }));
        setLevels(availableLevels);
        const availableAudioTracks = instance.audioTracks.map((track, index) => ({
          index,
          lang: track.lang ?? '',
          name: track.name,
        }));
        setHlsAudioTracks(availableAudioTracks);

        // Preferencias del estudiante entre lecciones: calidad fija y idioma de audio.
        const storedQuality = readPref(PREF.quality);
        if (storedQuality && storedQuality !== 'auto') {
          const wanted = Number.parseInt(storedQuality, 10);
          if (Number.isFinite(wanted)) {
            let bestIndex = -1;
            let bestDiff = Number.POSITIVE_INFINITY;
            instance.levels.forEach((level, index) => {
              if (!level.height) return;
              const diff = Math.abs(level.height - wanted);
              if (diff < bestDiff) {
                bestDiff = diff;
                bestIndex = index;
              }
            });
            if (bestIndex >= 0) {
              instance.currentLevel = bestIndex;
              setSelectedLevel(bestIndex);
            }
          }
        }
        const storedAudio = readPref(PREF.audio);
        if (storedAudio) {
          const match = availableAudioTracks.find((track) => track.lang === storedAudio);
          if (match && match.index !== instance.audioTrack) {
            instance.audioTrack = match.index;
          }
        }
        setSelectedAudioIndex(instance.audioTrack);

        manifestReadyRef.current = true;
        applyResume();
        if (autoPlayRef.current && videoRef.current) {
          videoRef.current.play().catch(() => {
            // El navegador puede bloquear el autoplay: el estudiante pulsa play.
          });
        }
      });

      instance.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
        setSelectedLevel(data.level);
      });

      instance.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_, data) => {
        setSelectedAudioIndex(data.id);
      });

      instance.on(Hls.Events.ERROR, (_, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          setFatalError('La reproducción se interrumpió. Verifica tu conexión e inténtalo de nuevo.');
          return;
        }
        // Non-fatal errors (a dropped fragment, a stalled network request) recover on
        // their own most of the time, but a silent console.error leaves the student
        // staring at a frozen frame with no explanation — surface a brief, dismissable
        // "reconnecting" cue instead of nothing.
        setReconnecting(true);
        window.setTimeout(() => setReconnecting(false), 4000);
      });

      hlsRef.current = instance;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = meta.hls_url;
      const onNativeError = () => setFatalError('La reproducción se interrumpió. Verifica tu conexión e inténtalo de nuevo.');
      // HLS nativo (iOS/Safari): no hay evento de manifiesto; los metadatos listos hacen su papel.
      const onNativeMetadata = () => {
        manifestReadyRef.current = true;
        applyResume();
        if (autoPlayRef.current) {
          video.play().catch(() => {
            // autoplay bloqueado: el estudiante pulsa play
          });
        }
      };
      video.addEventListener('error', onNativeError);
      video.addEventListener('loadedmetadata', onNativeMetadata, { once: true });
      return () => {
        video.removeEventListener('error', onNativeError);
        video.removeEventListener('loadedmetadata', onNativeMetadata);
      };
    } else {
      setError('Este navegador no soporta la reproducción HLS.');
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.hls_url, retryKey]);

  // Restore the viewer's last volume and detect Picture-in-Picture support once.
  useEffect(() => {
    const stored = readStoredVolume();
    setVolume(stored);
    setPipSupported(typeof document !== 'undefined' && Boolean(document.pictureInPictureEnabled));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      setIsPlaying(true);
      setEnded(false);
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onCanPlay = () => setIsBuffering(false);
    const onVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };
    const onRateChange = () => setPlaybackRate(video.playbackRate);
    const onEnterPiP = () => setIsPiP(true);
    const onLeavePiP = () => setIsPiP(false);
    const onTimeUpdateHandler = () => {
      const ct = video.currentTime || 0;
      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      setCurrentTime(ct);
      if (dur > 0) {
        setDuration(dur);
        durationRef.current = dur;
        if (ct < dur - 1) setEnded(false);
      }
      if (video.buffered.length > 0 && dur > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        setBufferedPercent(Math.min(100, (bufferedEnd / dur) * 100));
      }
      onTimeUpdateRef.current?.(ct, dur);

      // Control multimedia del sistema (pantalla de bloqueo): posición, como mucho 1 vez por segundo.
      if (dur > 0 && 'mediaSession' in navigator) {
        const now = Date.now();
        if (now - positionUpdateRef.current > 1000) {
          positionUpdateRef.current = now;
          try {
            navigator.mediaSession.setPositionState({
              duration: dur,
              position: Math.min(ct, dur),
              playbackRate: video.playbackRate || 1,
            });
          } catch {
            // valores fuera de rango durante la carga: se ignora
          }
        }
      }
    };
    // La finalización de la lección la decide el servidor (onEnded); la tarjeta de cierre solo ayuda a continuar.
    const onEndedHandler = () => {
      onEndedRef.current?.();
      setEnded(true);
    };
    const onLoadedMetadata = () => {
      video.volume = readStoredVolume();
      video.muted = readBoolPref(PREF.muted, false);
      const storedRate = readNumberPref(PREF.rate, 1);
      if (PLAYBACK_RATES.includes(storedRate)) video.playbackRate = storedRate;
      applyResume();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('ratechange', onRateChange);
    video.addEventListener('enterpictureinpicture', onEnterPiP);
    video.addEventListener('leavepictureinpicture', onLeavePiP);
    video.addEventListener('timeupdate', onTimeUpdateHandler);
    video.addEventListener('progress', onTimeUpdateHandler);
    video.addEventListener('loadedmetadata', onTimeUpdateHandler);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEndedHandler);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('ratechange', onRateChange);
      video.removeEventListener('enterpictureinpicture', onEnterPiP);
      video.removeEventListener('leavepictureinpicture', onLeavePiP);
      video.removeEventListener('timeupdate', onTimeUpdateHandler);
      video.removeEventListener('progress', onTimeUpdateHandler);
      video.removeEventListener('loadedmetadata', onTimeUpdateHandler);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('ended', onEndedHandler);
    };
    // The <video> element only enters the DOM once meta.status === 'ready' (see
    // the early returns above) — re-run once that happens so videoRef.current
    // isn't still null from the loading-state render that mounted this effect.
  }, [meta?.status, applyResume]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Atasco: si el video espera más de 8 s avisamos y, en calidad Auto, bajamos el techo de calidad.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const clearStall = () => {
      if (stallTimerRef.current) {
        window.clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
      setSlowNotice(false);
    };
    const onWaitingStall = () => {
      if (stallTimerRef.current) return;
      stallTimerRef.current = window.setTimeout(() => {
        stallTimerRef.current = null;
        if (!navigator.onLine) return; // lo cubre el aviso de "sin conexión"
        setSlowNotice(true);
        const hls = hlsRef.current;
        if (hls && hls.autoLevelEnabled) {
          const current = hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel;
          if (current > 0) hls.autoLevelCapping = current - 1;
        }
      }, STALL_NOTICE_MS);
    };

    video.addEventListener('waiting', onWaitingStall);
    video.addEventListener('playing', clearStall);
    video.addEventListener('canplay', clearStall);
    video.addEventListener('pause', clearStall);
    return () => {
      video.removeEventListener('waiting', onWaitingStall);
      video.removeEventListener('playing', clearStall);
      video.removeEventListener('canplay', clearStall);
      video.removeEventListener('pause', clearStall);
      if (stallTimerRef.current) window.clearTimeout(stallTimerRef.current);
    };
  }, [meta?.status]);

  // Sin conexión: recordar si estaba reproduciendo y reanudar solo al volver la red.
  useEffect(() => {
    const video = videoRef.current;
    if (!online) {
      wasOfflineRef.current = true;
      wasPlayingOfflineRef.current = Boolean(video && !video.paused);
      return;
    }
    if (!wasOfflineRef.current) return;
    wasOfflineRef.current = false;
    hlsRef.current?.startLoad();
    if (wasPlayingOfflineRef.current && video?.paused) {
      video.play().catch(() => {
        // el navegador puede exigir un gesto: el estudiante pulsa play
      });
    }
    wasPlayingOfflineRef.current = false;
  }, [online]);

  // Controles multimedia del sistema (pantalla de bloqueo, auriculares).
  useEffect(() => {
    if (meta?.status !== 'ready' || !('mediaSession' in navigator)) return;
    const session = navigator.mediaSession;
    const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // acción no soportada en este navegador
      }
    };
    try {
      session.metadata = new MediaMetadata({ title, artist: 'SateLab Academy' });
    } catch {
      // MediaMetadata no disponible
    }
    setHandler('play', () => {
      videoRef.current?.play().catch(() => undefined);
    });
    setHandler('pause', () => videoRef.current?.pause());
    setHandler('seekbackward', (details) => {
      const video = videoRef.current;
      if (video) video.currentTime = Math.max(0, video.currentTime - (details.seekOffset ?? 10));
    });
    setHandler('seekforward', (details) => {
      const video = videoRef.current;
      if (video) video.currentTime = Math.min(video.duration || Infinity, video.currentTime + (details.seekOffset ?? 10));
    });
    setHandler('previoustrack', hasPrevious ? () => onPreviousRef.current?.() : null);
    setHandler('nexttrack', hasNextLesson ? () => nextLessonRef.current?.onNext() : null);
    return () => {
      (['play', 'pause', 'seekbackward', 'seekforward', 'previoustrack', 'nexttrack'] as MediaSessionAction[]).forEach(
        (action) => setHandler(action, null),
      );
      try {
        session.metadata = null;
      } catch {
        // nada que limpiar
      }
    };
  }, [meta?.status, title, hasPrevious, hasNextLesson]);

  // Al abrir la lección el foco va al reproductor para que los atajos funcionen sin hacer clic.
  useEffect(() => {
    if (meta?.status === 'ready') {
      containerRef.current?.focus({ preventScroll: true });
    }
  }, [meta?.status, videoId]);

  // Auto-hide controls while playing, like a modern streaming player — always
  // visible while paused or while a settings menu is open.
  const scheduleHide = useCallback(() => {
    if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = window.setTimeout(() => {
      setShowControls((prev) => prev);
      setIsPlayingGuardedHide();
    }, HIDE_CONTROLS_DELAY_MS);
  }, []);

  // Reads latest isPlaying/settingsPane without re-creating the callback above.
  const isPlayingRef = useRef(isPlaying);
  const settingsPaneRef = useRef(settingsPane);
  const showControlsRef = useRef(showControls);
  useLayoutEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useLayoutEffect(() => { settingsPaneRef.current = settingsPane; }, [settingsPane]);
  useLayoutEffect(() => { showControlsRef.current = showControls; }, [showControls]);

  function setIsPlayingGuardedHide() {
    if (isPlayingRef.current && settingsPaneRef.current === 'closed') {
      setShowControls(false);
    }
  }

  const wakeControls = useCallback(() => {
    setShowControls(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    if (isPlaying) {
      scheduleHide();
    } else {
      if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
      setShowControls(true);
    }
  }, [isPlaying, scheduleHide]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) window.clearTimeout(hideTimeoutRef.current);
      if (clickTimeoutRef.current) window.clearTimeout(clickTimeoutRef.current);
      if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
      if (tapTimerRef.current) window.clearTimeout(tapTimerRef.current);
      if (noteTimeoutRef.current) window.clearTimeout(noteTimeoutRef.current);
    };
  }, []);

  const togglePlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      try {
        await video.play();
      } catch (err) {
        // AbortError fires when the play() request gets interrupted by a new
        // load request — e.g. the player unmounts mid-play because the user
        // navigated away, or a rapid double click issues a second play()
        // before the first settles. Expected, not a real playback failure;
        // anything else still surfaces so real errors aren't swallowed.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('Video play() error:', err);
      }
    } else {
      video.pause();
    }
  }, []);

  const seekBy = useCallback((deltaSeconds: number) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    video.currentTime = Math.min(duration, Math.max(0, video.currentTime + deltaSeconds));
  }, [duration]);

  const setVolumeSafe = useCallback((next: number) => {
    const video = videoRef.current;
    if (!video) return;
    const clamped = Math.min(1, Math.max(0, next));
    video.volume = clamped;
    video.muted = clamped === 0;
    writeStoredVolume(clamped);
    writePref(PREF.muted, String(video.muted));
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.muted || video.volume === 0) {
      video.muted = false;
      video.volume = video.volume === 0 ? readStoredVolume() || 1 : video.volume;
    } else {
      video.muted = true;
    }
    writePref(PREF.muted, String(video.muted));
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }
    if (typeof container.requestFullscreen === 'function') {
      await container.requestFullscreen();
      return;
    }
    // Safari/iOS never implements Element.requestFullscreen on a plain container — only
    // HTMLVideoElement.webkitEnterFullscreen(), which puts just the <video> (not the custom
    // controls overlay) into native fullscreen. Best available fallback on that platform.
    const video = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    video?.webkitEnterFullscreen?.();
  }, []);

  const togglePiP = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch (err) {
      console.error('PiP error:', err);
    }
  }, []);

  const applyRate = useCallback((rate: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    writePref(PREF.rate, String(rate));
  }, []);

  const setRate = useCallback((rate: number) => {
    applyRate(rate);
    setSettingsPane('closed');
  }, [applyRate]);

  const stepRate = useCallback((direction: 1 | -1) => {
    const video = videoRef.current;
    if (!video) return;
    const currentIndex = PLAYBACK_RATES.findIndex((rate) => rate >= video.playbackRate - 0.001);
    const base = currentIndex === -1 ? PLAYBACK_RATES.length - 1 : currentIndex;
    const next = PLAYBACK_RATES[Math.min(PLAYBACK_RATES.length - 1, Math.max(0, base + direction))];
    applyRate(next);
    flashNote(next === 1 ? 'Velocidad normal' : `Velocidad ${next}x`);
  }, [applyRate, flashNote]);

  const setLevel = useCallback((level: number) => {
    setSelectedLevel(level);
    if (hlsRef.current) {
      hlsRef.current.currentLevel = level;
      // Elegir una calidad fija levanta el techo que pudo poner un atasco previo.
      if (level === -1) hlsRef.current.autoLevelCapping = -1;
      const height = level === -1 ? null : hlsRef.current.levels[level]?.height;
      writePref(PREF.quality, height ? String(height) : 'auto');
    }
    setSettingsPane('closed');
  }, []);

  const setAudioTrackIndex = useCallback((index: number) => {
    if (hlsRef.current) {
      hlsRef.current.audioTrack = index;
      const lang = hlsRef.current.audioTracks[index]?.lang;
      if (lang) writePref(PREF.audio, lang);
    }
    setSettingsPane('closed');
  }, []);

  const toggleCaptions = useCallback(() => {
    setCaptionsEnabled((prev) => {
      writePref(PREF.captions, String(!prev));
      return !prev;
    });
  }, []);

  const replay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setEnded(false);
    video.currentTime = 0;
    video.play().catch(() => undefined);
  }, []);

  // Reintentar tras un corte: renueva el acceso (token de vida corta) y retoma donde iba.
  const handleRetry = useCallback(() => {
    const video = videoRef.current;
    retryTargetRef.current = video?.currentTime ?? 0;
    resumeAppliedRef.current = false;
    setFatalError(null);
    loadMeta(true);
  }, [loadMeta]);

  const onScrub = useCallback((ratio: number) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    video.currentTime = duration * ratio;
    setCurrentTime(video.currentTime);
  }, [duration]);

  // Los saltos sucesivos del mismo lado se acumulan en un solo aviso ("+20 s").
  const triggerSeekFlash = useCallback((side: 'left' | 'right', step = 10) => {
    if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
    setSeekFlash((prev) =>
      prev && prev.side === side
        ? { ...prev, amount: prev.amount + step }
        : { side, id: Date.now(), amount: step },
    );
    flashTimeoutRef.current = window.setTimeout(() => setSeekFlash(null), 900);
  }, []);

  const handleVideoAreaClick = useCallback(() => {
    // En pantallas táctiles los toques se resuelven en handleSurfacePointerUp.
    if (lastPointerTypeRef.current === 'touch') return;
    if (clickTimeoutRef.current) {
      window.clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = window.setTimeout(() => {
      togglePlay();
      clickTimeoutRef.current = null;
    }, 220);
  }, [togglePlay]);

  const handleVideoAreaDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (lastPointerTypeRef.current === 'touch') return;
    if (clickTimeoutRef.current) {
      window.clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratioX = (event.clientX - rect.left) / rect.width;
    if (ratioX < 0.4) {
      seekBy(-10);
      triggerSeekFlash('left');
    } else if (ratioX > 0.6) {
      seekBy(10);
      triggerSeekFlash('right');
    } else {
      void toggleFullscreen();
    }
  }, [seekBy, toggleFullscreen, triggerSeekFlash]);

  const toggleControlsTouch = useCallback(() => {
    if (showControlsRef.current && isPlayingRef.current) {
      setShowControls(false);
    } else {
      wakeControls();
    }
  }, [wakeControls]);

  const handleSurfacePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    lastPointerTypeRef.current = event.pointerType;
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  // Táctil: un toque muestra/oculta los controles; doble toque en los tercios salta ±10 s
  // (y los toques siguientes acumulan); doble toque al centro pausa o reproduce.
  const handleSurfacePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratioX = (event.clientX - rect.left) / rect.width;
    const zone: TapZone = ratioX < 1 / 3 ? 'left' : ratioX > 2 / 3 ? 'right' : 'center';
    const now = Date.now();
    const previousTap = lastTapRef.current;
    const previousSeek = lastSeekTapRef.current;
    const isDoubleTap = now - previousTap.time < DOUBLE_TAP_MS && previousTap.zone === zone;
    const isChain = zone !== 'center' && now - previousSeek.time < SEEK_CHAIN_MS && previousSeek.zone === zone;

    if (isDoubleTap || isChain) {
      if (tapTimerRef.current) {
        window.clearTimeout(tapTimerRef.current);
        tapTimerRef.current = null;
      }
      if (zone === 'center') {
        lastTapRef.current = { time: 0, zone };
        void togglePlay();
      } else {
        lastTapRef.current = { time: now, zone };
        lastSeekTapRef.current = { time: now, zone };
        seekBy(zone === 'left' ? -10 : 10);
        triggerSeekFlash(zone);
      }
      return;
    }

    lastTapRef.current = { time: now, zone };
    if (tapTimerRef.current) window.clearTimeout(tapTimerRef.current);
    tapTimerRef.current = window.setTimeout(() => {
      tapTimerRef.current = null;
      toggleControlsTouch();
    }, DOUBLE_TAP_MS);
  }, [seekBy, toggleControlsTouch, togglePlay, triggerSeekFlash]);

  // Arrastre de la barra de progreso (ratón, táctil o lápiz).
  const scrubbingRef = useRef(false);
  const ratioFromPointer = useCallback((clientX: number) => {
    const rect = scrubberRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  // Player-scoped keyboard control via hotkeys-js. Bound at the document level and
  // gated by a per-instance scope (hotkeys.setScope) that turns on while the pointer
  // is over the player OR keyboard focus is anywhere inside it — not tied to a single
  // focusable div, so shortcuts keep working after closing the settings menu, unlike
  // the previous onKeyDown-on-a-div approach.
  const hotkeysScope = useMemo(() => `netflix-player-${videoId}`, [videoId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const activate = () => hotkeys.setScope(hotkeysScope);
    const deactivate = (relatedTarget: EventTarget | null) => {
      if (relatedTarget instanceof Node && container.contains(relatedTarget)) return;
      if (hotkeys.getScope() === hotkeysScope) hotkeys.setScope('all');
    };
    const onMouseLeave = () => deactivate(null);
    const onFocusOut = (event: FocusEvent) => deactivate(event.relatedTarget);

    container.addEventListener('mouseenter', activate);
    container.addEventListener('focusin', activate);
    container.addEventListener('mouseleave', onMouseLeave);
    container.addEventListener('focusout', onFocusOut);

    return () => {
      container.removeEventListener('mouseenter', activate);
      container.removeEventListener('focusin', activate);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('focusout', onFocusOut);
      if (hotkeys.getScope() === hotkeysScope) hotkeys.setScope('all');
    };
  }, [hotkeysScope]);

  useEffect(() => {
    const keyList =
      'space,k,left,right,l,j,up,down,m,f,home,end,0,1,2,3,4,5,6,7,8,9,escape';

    const handler = (event: KeyboardEvent, handlerEvent: HotkeysEvent) => {
      if (isTypingTarget(event.target)) return;
      const key = handlerEvent.key;
      // Espacio sobre un botón o ítem de menú enfocado solo lo activa (no también reproduce).
      if (key === 'space' && closestInteractive(event.target, 'button,a[href],[role="menuitem"],[role="menuitemradio"]')) return;
      // La barra de progreso enfocada maneja sus propias flechas, Inicio y Fin.
      if (
        (key === 'left' || key === 'right' || key === 'home' || key === 'end') &&
        closestInteractive(event.target, '[role="slider"]')
      ) {
        return;
      }
      wakeControls();

      switch (key) {
        case 'space':
        case 'k':
          event.preventDefault();
          void togglePlay();
          break;
        case 'right':
          event.preventDefault();
          seekBy(5);
          triggerSeekFlash('right', 5);
          break;
        case 'left':
          event.preventDefault();
          seekBy(-5);
          triggerSeekFlash('left', 5);
          break;
        case 'l':
          event.preventDefault();
          seekBy(10);
          triggerSeekFlash('right');
          break;
        case 'j':
          event.preventDefault();
          seekBy(-10);
          triggerSeekFlash('left');
          break;
        case 'up':
          event.preventDefault();
          setVolumeSafe(volume + 0.05);
          break;
        case 'down':
          event.preventDefault();
          setVolumeSafe(volume - 0.05);
          break;
        case 'm':
          event.preventDefault();
          toggleMute();
          break;
        case 'f':
          event.preventDefault();
          void toggleFullscreen();
          break;
        case 'home':
          event.preventDefault();
          if (videoRef.current) videoRef.current.currentTime = 0;
          break;
        case 'end':
          // Un segundo antes del final: así Fin no dispara 'ended' (ni la finalización) por sí solo.
          event.preventDefault();
          if (videoRef.current && duration) videoRef.current.currentTime = Math.max(0, duration - 1);
          break;
        case 'escape':
          if (settingsPaneRef.current !== 'closed') {
            setSettingsPane('closed');
          } else if (document.fullscreenElement) {
            void document.exitFullscreen();
          }
          break;
        default: {
          if (/^[0-9]$/.test(key) && duration) {
            const target = (Number.parseInt(key, 10) / 10) * duration;
            const video = videoRef.current;
            if (video) video.currentTime = target;
          }
        }
      }
    };

    hotkeys(keyList, { scope: hotkeysScope, keydown: true }, handler);
    return () => hotkeys.unbind(keyList, hotkeysScope, handler);
  }, [duration, hotkeysScope, seekBy, setVolumeSafe, toggleFullscreen, toggleMute, togglePlay, triggerSeekFlash, volume, wakeControls]);

  // Teclas fuera del mapa de hotkeys-js (puntuación y letras nuevas); mismo alcance: ratón o foco dentro del reproductor.
  const captionsAvailableRef = useRef(false);
  const pipSupportedRef = useRef(false);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (hotkeys.getScope() !== hotkeysScope) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      let handled = true;
      switch (event.key) {
        case 'c':
        case 'C':
          if (captionsAvailableRef.current) {
            toggleCaptions();
            flashNote('Subtítulos');
          } else {
            handled = false;
          }
          break;
        case ',':
        case '<':
          stepRate(-1);
          break;
        case '.':
        case '>':
          stepRate(1);
          break;
        case 'p':
        case 'P':
          if (pipSupportedRef.current) void togglePiP();
          else handled = false;
          break;
        case 'n':
        case 'N':
          if (nextLessonRef.current) nextLessonRef.current.onNext();
          else handled = false;
          break;
        case 't':
        case 'T':
          if (onToggleTheaterRef.current) onToggleTheaterRef.current();
          else handled = false;
          break;
        case '?':
          setSettingsPane('shortcuts');
          break;
        default:
          handled = false;
      }
      if (handled) {
        event.preventDefault();
        wakeControls();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [flashNote, hotkeysScope, stepRate, toggleCaptions, togglePiP, wakeControls]);

  // Pista de atajos: una sola vez, en equipos con teclado y ratón.
  const dismissHint = useCallback(() => {
    setShowHint(false);
    writePref(PREF.hintSeen, 'true');
  }, []);
  useEffect(() => {
    if (!isPlaying || hintShownRef.current) return;
    hintShownRef.current = true;
    if (readPref(PREF.hintSeen)) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    setShowHint(true);
    const id = window.setTimeout(dismissHint, 7000);
    return () => window.clearTimeout(id);
  }, [isPlaying, dismissHint]);

  const hoverPreview = useMemo(() => {
    if (hoveredRatio === null || previewCues.length === 0 || !duration) {
      return null;
    }
    const targetTime = duration * hoveredRatio;
    return previewCues.find((cue) => targetTime >= cue.start && targetTime < cue.end) || previewCues[0];
  }, [duration, hoveredRatio, previewCues]);

  const VolumeIcon = isMuted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const currentQualityLabel = selectedLevel === -1
    ? 'Auto'
    : levels.find((level) => level.index === selectedLevel)?.label ?? 'Auto';

  const selectedHlsAudioTrack = hlsAudioTracks.find((track) => track.index === selectedAudioIndex);
  // hls.js audio tracks and meta.audio_tracks are matched by LANGUAGE, not
  // array position — a track added after the video was first processed can
  // sit at a different index in each list (design.md §7).
  const activeMetaAudioTrack = useMemo(
    () => meta?.audio_tracks.find((track) => track.language_code === selectedHlsAudioTrack?.lang),
    [meta?.audio_tracks, selectedHlsAudioTrack?.lang],
  );
  const activeCaptionsUrl = activeMetaAudioTrack?.captions_url ?? null;

  useLayoutEffect(() => {
    captionsAvailableRef.current = Boolean(activeCaptionsUrl);
    pipSupportedRef.current = pipSupported;
  }, [activeCaptionsUrl, pipSupported]);

  useEffect(() => {
    const video = videoRef.current;
    const track = video?.textTracks[0];
    if (!track) return;
    track.mode = captionsEnabled ? 'showing' : 'hidden';
    // Subtítulos por encima de la barra de controles mientras esta se ve.
    const cues = track.cues;
    if (cues) {
      for (let index = 0; index < cues.length; index += 1) {
        const cue = cues[index] as VTTCue;
        cue.snapToLines = true;
        cue.line = showControls ? -4 : 'auto';
      }
    }
  }, [captionsEnabled, activeCaptionsUrl, showControls, cuesVersion]);

  if (loadingMeta) {
    return (
      <div
        role="status"
        aria-label="Cargando el video"
        className="motion-shimmer flex aspect-video w-full items-center justify-center rounded-xl bg-neutral-900"
      >
        <Play className="h-10 w-10 text-white/20" aria-hidden="true" />
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-xl bg-black px-6 text-center text-sm text-red-300">
        <p>{error || 'No se pudo inicializar el video.'}</p>
        {error?.includes('no soporta') ? (
          <p className="text-xs text-slate-400">Prueba con Chrome, Edge, Safari o Firefox actualizados.</p>
        ) : (
          <button
            type="button"
            onClick={() => loadMeta(false)}
            className="min-h-11 rounded-full border border-white/30 px-5 text-xs font-medium text-white transition-colors hover:bg-white/10"
          >
            Reintentar
          </button>
        )}
      </div>
    );
  }

  if (meta.status !== 'ready') {
    const statusMessage =
      meta.status === 'error'
        ? 'Hubo un problema al procesar este video. Escríbenos si persiste.'
        : 'Estamos preparando este video. Vuelve en unos minutos.';
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl bg-black px-6 text-center text-slate-200">
        <p className="text-base font-semibold">{meta.status === 'error' ? 'Video no disponible' : 'Video en proceso'}</p>
        <p className="text-xs text-slate-400">{statusMessage}</p>
      </div>
    );
  }

  const liveMessage = !online
    ? 'Sin conexión. El video se reanudará cuando vuelvas a conectarte.'
    : fatalError
      ? fatalError
      : slowNotice
        ? 'Tu conexión es lenta. Estamos bajando la calidad.'
        : reconnecting
          ? 'Reconectando…'
          : ended
            ? 'Lección terminada.'
            : note ?? '';

  return (
    <div
      ref={containerRef}
      className={`academy-player group/player relative aspect-video w-full max-h-[calc(100dvh-6rem)] overflow-hidden rounded-xl bg-black outline-none focus-visible:ring-2 focus-visible:ring-ring/70 ${
        showControls ? '' : 'cursor-none'
      }`}
      tabIndex={0}
      aria-label={`Reproductor de video: ${title}`}
      onMouseMove={wakeControls}
      onMouseLeave={() => {
        if (isPlaying && settingsPane === 'closed') setShowControls(false);
      }}
    >
      <style>{`.academy-player video::cue{background:rgba(0,0,0,.75);color:#fff;font-size:1.1em;text-shadow:none}`}</style>

      <video ref={videoRef} className="h-full w-full" title={title} playsInline crossOrigin="anonymous">
        {activeCaptionsUrl && (
          <track
            key={activeCaptionsUrl}
            kind="subtitles"
            src={activeCaptionsUrl}
            srcLang={activeMetaAudioTrack?.language_code}
            default={false}
            onLoad={() => setCuesVersion((version) => version + 1)}
          />
        )}
      </video>

      {/* Anuncios para lectores de pantalla: conexión, carga lenta, errores y fin de lección. */}
      <div role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </div>

      {/* Superficie de toque / clic. Ratón: clic reproduce, doble clic salta ±10 s (o pantalla completa al centro).
          Táctil: toque muestra controles, doble toque salta o pausa (ver handleSurfacePointerUp). */}
      <div
        className="absolute inset-0 z-10 touch-manipulation"
        onPointerDown={handleSurfacePointerDown}
        onPointerUp={handleSurfacePointerUp}
        onClick={handleVideoAreaClick}
        onDoubleClick={handleVideoAreaDoubleClick}
      />

      <VideoWatermarkOverlay logoUrl={WATERMARK_LOGO_URL} watermarkCode={meta.watermark_code} />

      {isBuffering && !fatalError && online && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-white/90 motion-reduce:animate-none" aria-hidden="true" />
        </div>
      )}

      {!online && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-30 flex max-w-[90%] -translate-x-1/2 items-center gap-2 rounded-full bg-black/80 px-4 py-1.5 text-xs text-white">
          <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>Sin conexión. El video se reanudará cuando vuelvas a conectarte.</span>
        </div>
      )}

      {online && slowNotice && !fatalError && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-30 max-w-[90%] -translate-x-1/2 rounded-full bg-black/80 px-4 py-1.5 text-center text-xs text-white">
          {selectedLevel === -1
            ? 'Tu conexión es lenta. Estamos bajando la calidad.'
            : 'Tu conexión es lenta. Prueba con una calidad menor o con Auto.'}
        </div>
      )}

      {online && reconnecting && !slowNotice && !fatalError && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-xs text-white/90">
          Reconectando…
        </div>
      )}

      {note && (
        <div className="motion-fade pointer-events-none absolute left-1/2 top-12 z-20 -translate-x-1/2 rounded-full bg-black/75 px-3 py-1 text-xs font-medium text-white">
          {note}
        </div>
      )}

      {showHint && (
        <div className="motion-fade absolute left-4 top-12 z-30 flex items-center gap-2 rounded-lg border border-white/15 bg-black/85 py-1.5 pl-3 pr-1.5 text-xs text-white">
          <span>
            Pulsa <kbd className="rounded border border-white/25 bg-white/10 px-1.5 py-0.5 font-sans font-semibold">?</kbd> para ver los atajos
          </span>
          <button
            type="button"
            onClick={dismissHint}
            aria-label="Cerrar sugerencia"
            className="flex h-7 w-7 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {resumeToast !== null && !ended && (
        <ResumeToast
          seconds={resumeToast}
          onDismiss={() => setResumeToast(null)}
          onRestart={() => {
            const video = videoRef.current;
            if (video) video.currentTime = 0;
            setResumeToast(null);
          }}
        />
      )}

      {ended && !fatalError && (
        <EndOfVideoCard
          nextTitle={nextLesson?.title}
          onNext={nextLesson ? nextLesson.onNext : undefined}
          onReplay={replay}
        />
      )}

      {!isPlaying && !isBuffering && !fatalError && !ended && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm transition-transform duration-200 ease-out group-hover/player:scale-105">
            <Play className="h-7 w-7 translate-x-0.5 text-white" fill="currentColor" />
          </div>
        </div>
      )}

      {seekFlash && (
        <div
          key={seekFlash.id}
          aria-hidden="true"
          className={`pointer-events-none absolute top-1/2 z-20 -translate-y-1/2 ${
            seekFlash.side === 'left' ? 'left-[8%]' : 'right-[8%]'
          }`}
        >
          {/* El contenedor externo centra; el interno anima (solo opacidad/escala, y solo fundido con movimiento reducido). */}
          <div className="motion-pop flex flex-col items-center gap-1 rounded-full bg-black/60 px-5 py-4 text-white">
            {seekFlash.side === 'left' ? <RotateCcw className="h-6 w-6" /> : <RotateCw className="h-6 w-6" />}
            <span className="text-xs font-semibold tabular-nums">
              {seekFlash.side === 'left' ? '−' : '+'}
              {seekFlash.amount} s
            </span>
          </div>
        </div>
      )}

      {fatalError && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-black/90 px-6 text-center text-sm text-red-300">
          <p>{online ? fatalError : 'Sin conexión. Cuando vuelvas a conectarte podrás reintentar.'}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="min-h-11 rounded-full border border-white/30 px-5 text-xs font-medium text-white transition hover:bg-white/10"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Persistent thin progress line — stays visible even while the full control bar is hidden. */}
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[3px] bg-white/20 transition-opacity duration-300 ${
          showControls ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div
          className="h-full bg-primary"
          style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
        />
      </div>

      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/70 to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black via-black/60 to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        className={`absolute inset-x-0 top-0 z-20 px-4 pt-3 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <p className="truncate text-sm font-medium text-white/90">{title}</p>
      </div>

      <div
        onPointerDown={wakeControls}
        className={`absolute inset-x-0 bottom-0 z-20 flex flex-col gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div
          ref={scrubberRef}
          role="slider"
          tabIndex={0}
          aria-label="Progreso del video"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration || 0)}
          aria-valuenow={Math.round(currentTime)}
          aria-valuetext={`${formatTime(currentTime)} de ${formatTime(duration)}`}
          className="group/scrub relative flex h-4 cursor-pointer touch-none items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary pointer-coarse:h-8"
          onMouseEnter={() => setIsHoveringScrubber(true)}
          onMouseLeave={() => {
            setIsHoveringScrubber(false);
            setHoveredRatio(null);
          }}
          onMouseMove={(event) => {
            const ratio = ratioFromPointer(event.clientX);
            if (ratio !== null) setHoveredRatio(ratio);
          }}
          onPointerDown={(event) => {
            const ratio = ratioFromPointer(event.clientX);
            if (ratio === null) return;
            scrubbingRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            onScrub(ratio);
            if (event.pointerType === 'touch') {
              setIsHoveringScrubber(true);
              setHoveredRatio(ratio);
            }
          }}
          onPointerMove={(event) => {
            if (!scrubbingRef.current) return;
            const ratio = ratioFromPointer(event.clientX);
            if (ratio === null) return;
            onScrub(ratio);
            if (event.pointerType === 'touch') setHoveredRatio(ratio);
          }}
          onPointerUp={(event) => {
            scrubbingRef.current = false;
            if (event.pointerType === 'touch') {
              setIsHoveringScrubber(false);
              setHoveredRatio(null);
            }
          }}
          onPointerCancel={() => {
            scrubbingRef.current = false;
            setIsHoveringScrubber(false);
            setHoveredRatio(null);
          }}
          onKeyDown={(event) => {
            const video = videoRef.current;
            if (!video || !duration) return;
            const steps: Record<string, number> = { ArrowLeft: -5, ArrowRight: 5, PageDown: -10, PageUp: 10 };
            if (event.key in steps) {
              event.preventDefault();
              event.stopPropagation();
              seekBy(steps[event.key]);
            } else if (event.key === 'Home') {
              event.preventDefault();
              event.stopPropagation();
              video.currentTime = 0;
            } else if (event.key === 'End') {
              event.preventDefault();
              event.stopPropagation();
              video.currentTime = Math.max(0, duration - 1);
            }
          }}
        >
          <div className="absolute left-0 right-0 h-1 rounded-full bg-white/25 transition-[height] duration-150 group-hover/scrub:h-1.5 pointer-coarse:h-1.5" />
          <div
            className="absolute left-0 h-1 rounded-full bg-white/40 transition-[height] duration-150 group-hover/scrub:h-1.5 pointer-coarse:h-1.5"
            style={{ width: `${bufferedPercent}%` }}
          />
          <div
            className="absolute left-0 h-1 rounded-full bg-primary transition-[height] duration-150 group-hover/scrub:h-1.5 pointer-coarse:h-1.5"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
          <div
            className="absolute h-3 w-3 -translate-x-1/2 scale-0 rounded-full bg-primary transition-transform duration-150 group-hover/scrub:scale-100 group-focus-visible/scrub:scale-100 pointer-coarse:h-4 pointer-coarse:w-4 pointer-coarse:scale-100"
            style={{ left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />

          {isHoveringScrubber && hoveredRatio !== null && (
            <div
              className="pointer-events-none absolute top-1/2 h-6 w-[2px] -translate-x-1/2 -translate-y-1/2 bg-white/80"
              style={{ left: `${hoveredRatio * 100}%` }}
            />
          )}

          {isHoveringScrubber && hoveredRatio !== null && (
            <div
              className="pointer-events-none absolute bottom-6 z-20 -translate-x-1/2 overflow-hidden rounded-md border border-white/20 bg-black"
              style={{ left: `${Math.max(8, Math.min(hoveredRatio * 100, 92))}%` }}
            >
              {hoverPreview && meta.sprite_url && (
                <div
                  style={{
                    width: '240px',
                    height: '135px',
                    backgroundImage: `url(${meta.sprite_url})`,
                    backgroundPosition: `-${hoverPreview.x}px -${hoverPreview.y}px`,
                  }}
                />
              )}
              <div className="border-t border-white/20 px-2 py-1 text-center text-micro text-white">
                {formatTime(duration * hoveredRatio)}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void togglePlay()}
              aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
              className="flex h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
            >
              {isPlaying ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="h-5 w-5 translate-x-0.5" fill="currentColor" />}
            </button>

            <div className="group/volume flex items-center gap-2">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={isMuted ? 'Activar sonido' : 'Silenciar'}
                className="flex h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <VolumeIcon className="h-5 w-5" />
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(event) => setVolumeSafe(Number.parseFloat(event.target.value))}
                aria-label="Volumen"
                className="h-1 w-0 origin-left cursor-pointer appearance-none overflow-hidden rounded-full bg-white/25 opacity-0 transition-all duration-200 group-hover/volume:w-20 group-hover/volume:opacity-100 accent-white"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowRemaining((prev) => !prev)}
              aria-label={showRemaining ? 'Mostrar tiempo transcurrido y duración' : 'Mostrar tiempo restante'}
              className="rounded px-1 text-xs tabular-nums text-white/90 transition hover:text-white"
            >
              {showRemaining
                ? `-${formatTime(Math.max(0, duration - currentTime))}`
                : `${formatTime(currentTime)} / ${formatTime(duration)}`}
            </button>
          </div>

          <div className="flex items-center gap-1">
            <div className="relative">
              <button
                type="button"
                onClick={() => setSettingsPane((prev) => (prev === 'closed' ? 'main' : 'closed'))}
                aria-label="Ajustes"
                aria-haspopup="menu"
                aria-expanded={settingsPane !== 'closed'}
                className="flex h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <Settings className={`h-5 w-5 transition-transform duration-300 ${settingsPane !== 'closed' ? 'rotate-45' : ''}`} />
              </button>

              {settingsPane !== 'closed' && (
                <>
                  <button
                    type="button"
                    aria-label="Cerrar ajustes"
                    onClick={() => setSettingsPane('closed')}
                    className="fixed inset-0 z-20 cursor-default"
                  />
                  <div role="menu" aria-label="Ajustes del video" className="absolute bottom-11 right-0 z-30 w-56 overflow-hidden rounded-xl border border-white/10 bg-black/95 text-sm text-white">
                    {settingsPane === 'main' && (
                      <div className="py-1">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => setSettingsPane('speed')}
                          className="flex w-full items-center justify-between px-3 py-2.5 transition hover:bg-white/10"
                        >
                          <span className="flex items-center gap-2"><Gauge className="h-4 w-4" /> Velocidad</span>
                          <span className="flex items-center gap-1 text-white/60">
                            {playbackRate === 1 ? 'Normal' : `${playbackRate}x`}
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => setSettingsPane('quality')}
                          className="flex w-full items-center justify-between px-3 py-2.5 transition hover:bg-white/10"
                        >
                          <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Calidad</span>
                          <span className="flex items-center gap-1 text-white/60">
                            {currentQualityLabel}
                            <ChevronRight className="h-4 w-4" />
                          </span>
                        </button>
                        {hlsAudioTracks.length > 1 && (
                          <button
                            type="button"
                            role="menuitem"
                          onClick={() => setSettingsPane('audio')}
                            className="flex w-full items-center justify-between px-3 py-2.5 transition hover:bg-white/10"
                          >
                            <span className="flex items-center gap-2"><Languages className="h-4 w-4" /> Audio</span>
                            <span className="flex items-center gap-1 text-white/60">
                              {selectedHlsAudioTrack?.name ?? 'Original'}
                              <ChevronRight className="h-4 w-4" />
                            </span>
                          </button>
                        )}
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => setSettingsPane('shortcuts')}
                          className="flex w-full items-center justify-between px-3 py-2.5 transition hover:bg-white/10"
                        >
                          <span className="flex items-center gap-2"><Keyboard className="h-4 w-4" /> Atajos de teclado</span>
                          <ChevronRight className="h-4 w-4 text-white/60" />
                        </button>
                      </div>
                    )}

                    {settingsPane === 'shortcuts' && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane('main')}
                          className="flex w-full items-center gap-2 border-b border-white/10 px-3 py-2.5 font-medium transition hover:bg-white/10"
                        >
                          <ChevronLeft className="h-4 w-4" /> Atajos de teclado
                        </button>
                        <div className="max-h-64 overflow-y-auto py-1">
                          {SHORTCUTS_LEGEND.map((item) => (
                            <div key={item.keys} className="flex items-center justify-between gap-3 px-3 py-2 text-white/90">
                              <span className="text-xs text-white/60">{item.label}</span>
                              <span className="rounded border border-white/20 bg-white/5 px-1.5 py-0.5 font-mono text-xs whitespace-nowrap">
                                {item.keys}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {settingsPane === 'audio' && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane('main')}
                          className="flex w-full items-center gap-2 border-b border-white/10 px-3 py-2.5 font-medium transition hover:bg-white/10"
                        >
                          <ChevronLeft className="h-4 w-4" /> Audio
                        </button>
                        {hlsAudioTracks.map((track) => (
                          <button
                            key={track.index}
                            type="button"
                            role="menuitemradio"
                            aria-checked={selectedAudioIndex === track.index}
                            onClick={() => setAudioTrackIndex(track.index)}
                            className="flex w-full items-center justify-between px-3 py-2 transition hover:bg-white/10"
                          >
                            <span>{track.name}</span>
                            {selectedAudioIndex === track.index && <Check className="h-4 w-4 text-primary" />}
                          </button>
                        ))}
                      </div>
                    )}

                    {settingsPane === 'speed' && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane('main')}
                          className="flex w-full items-center gap-2 border-b border-white/10 px-3 py-2.5 font-medium transition hover:bg-white/10"
                        >
                          <ChevronLeft className="h-4 w-4" /> Velocidad
                        </button>
                        {PLAYBACK_RATES.map((rate) => (
                          <button
                            key={rate}
                            type="button"
                            role="menuitemradio"
                            aria-checked={playbackRate === rate}
                            onClick={() => setRate(rate)}
                            className="flex w-full items-center justify-between px-3 py-2 transition hover:bg-white/10"
                          >
                            <span>{rate === 1 ? 'Normal' : `${rate}x`}</span>
                            {playbackRate === rate && <Check className="h-4 w-4 text-primary" />}
                          </button>
                        ))}
                      </div>
                    )}

                    {settingsPane === 'quality' && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane('main')}
                          className="flex w-full items-center gap-2 border-b border-white/10 px-3 py-2.5 font-medium transition hover:bg-white/10"
                        >
                          <ChevronLeft className="h-4 w-4" /> Calidad
                        </button>
                        <button
                          type="button"
                          role="menuitemradio"
                          aria-checked={selectedLevel === -1}
                          onClick={() => setLevel(-1)}
                          className="flex w-full items-center justify-between px-3 py-2 transition hover:bg-white/10"
                        >
                          <span>Auto</span>
                          {selectedLevel === -1 && <Check className="h-4 w-4 text-primary" />}
                        </button>
                        {levels.map((level) => (
                          <button
                            key={level.index}
                            type="button"
                            role="menuitemradio"
                            aria-checked={selectedLevel === level.index}
                            onClick={() => setLevel(level.index)}
                            className="flex w-full items-center justify-between px-3 py-2 transition hover:bg-white/10"
                          >
                            <span>{level.label}</span>
                            {selectedLevel === level.index && <Check className="h-4 w-4 text-primary" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {activeCaptionsUrl && (
              <button
                type="button"
                onClick={toggleCaptions}
                aria-label={captionsEnabled ? 'Desactivar subtítulos' : 'Activar subtítulos'}
                aria-pressed={captionsEnabled}
                className={`flex h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-full transition hover:bg-white/10 ${captionsEnabled ? 'text-primary' : 'text-white'}`}
              >
                <Captions className="h-5 w-5" />
              </button>
            )}

            {pipSupported && (
              <button
                type="button"
                onClick={() => void togglePiP()}
                aria-label={isPiP ? 'Salir de picture-in-picture' : 'Picture-in-picture'}
                className="flex h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <PictureInPicture2 className="h-4.5 w-4.5" />
              </button>
            )}

            {onToggleTheater && (
              <button
                type="button"
                onClick={onToggleTheater}
                aria-label={theater ? 'Salir del modo teatro' : 'Modo teatro'}
                aria-pressed={theater}
                className={`hidden h-9 w-9 items-center justify-center rounded-full transition hover:bg-white/10 lg:flex ${theater ? 'text-primary' : 'text-white'}`}
              >
                <RectangleHorizontal className="h-5 w-5" aria-hidden="true" />
              </button>
            )}

            <button
              type="button"
              onClick={() => void toggleFullscreen()}
              aria-label={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              className="flex h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-full text-white transition hover:bg-white/10"
            >
              {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
